import { getSupportTicketLeafFilters } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import { SPAM_TAG } from "@redotech/redo-support-tags-common/conversation-tag-record";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  ArrayStringFilterOperator,
  valuelessOperators,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import {
  DateFilterOperator,
  isRelativeTimeRange,
  KnownDateFilterTimeFrame,
  RelativeTimeUnit,
  type RelativeTime,
} from "@redotech/redo-views-common/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-views-common/advanced-filters/number-filter";
import { StringFilterOperator } from "@redotech/redo-views-common/advanced-filters/string-filter";
import type {
  AdvancedTableFilter,
  ArrayStringTableFilter,
  BooleanTableFilter,
  ExistsTableFilter,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import { getDateString } from "@redotech/util/date-format";
import { assertNever } from "@redotech/util/type";
import { getSupportTicketAdvancedFilters } from "./get-support-ticket-advanced-filters";
import { isRedoDefaultSupportTicketFilter } from "./get-support-ticket-filters-for-display";

export interface SupportTicketViewFilterSummary {
  Icon: AdvancedTableFilter["Icon"];
  operatorLabel: string;
  propertyName: string;
  valueLabel: string;
}

const arrayStringOperatorToText: Record<ArrayStringFilterOperator, string> = {
  [ArrayStringFilterOperator.CONTAINS]: "contains",
  [ArrayStringFilterOperator.NOT_CONTAINS]: "does not contain",
  [ArrayStringFilterOperator.MATCHES_EXACTLY]: "matches exactly",
  [ArrayStringFilterOperator.ANY_OF]: "include any of",
  [ArrayStringFilterOperator.NONE_OF]: "include none of",
  [ArrayStringFilterOperator.ALL_OF]: "have all of",
  [ArrayStringFilterOperator.IS_EMPTY]: "is empty",
  [ArrayStringFilterOperator.IS_NOT_EMPTY]: "is not empty",
  [ArrayStringFilterOperator.STARTS_WITH]: "starts with",
  [ArrayStringFilterOperator.DOES_NOT_START_WITH]: "does not start with",
};

const existsOperatorToText: Record<ExistsFilterOperator, string> = {
  [ExistsFilterOperator.EXISTS]: "is",
  [ExistsFilterOperator.NOT_EXISTS]: "is not",
};

const stringOperatorToText: Record<StringFilterOperator, string> = {
  [StringFilterOperator.EQUALS]: "is",
  [StringFilterOperator.NOT_EQUALS]: "is not",
  [StringFilterOperator.CONTAINS]: "contains",
};

const booleanOperatorToText: Record<BooleanFilterOperator, string> = {
  [BooleanFilterOperator.IS]: "is",
  [BooleanFilterOperator.IS_NOT]: "is not",
};

const dateOperatorToText: Record<DateFilterOperator, string> = {
  [DateFilterOperator.WITHIN]: "during",
  [DateFilterOperator.BEFORE]: "before",
  [DateFilterOperator.AFTER]: "after",
};

const numberOperatorToText: Record<NumberFilterOperator, string> = {
  [NumberFilterOperator.EQUAL]: "equals",
  [NumberFilterOperator.LESS_THAN]: "less than",
  [NumberFilterOperator.GREATER_THAN]: "greater than",
  [NumberFilterOperator.BETWEEN]: "between",
};

const timeFrameToText: Record<KnownDateFilterTimeFrame, string> = {
  [KnownDateFilterTimeFrame.TODAY]: "Today",
  [KnownDateFilterTimeFrame.THIS_WEEK]: "This week",
  [KnownDateFilterTimeFrame.LAST_WEEK]: "Last week",
  [KnownDateFilterTimeFrame.THIS_MONTH]: "This month",
  [KnownDateFilterTimeFrame.LAST_MONTH]: "Last month",
  [KnownDateFilterTimeFrame.THIS_YEAR]: "This year",
  [KnownDateFilterTimeFrame.LAST_YEAR]: "Last year",
  [KnownDateFilterTimeFrame.CUSTOM]: "Custom",
  [KnownDateFilterTimeFrame.RELATIVE]: "Relative",
};

function arrayOperatorLabel(
  operator: ArrayStringFilterOperator,
  valueCount: number,
): string {
  if (operator === ArrayStringFilterOperator.ANY_OF) {
    return valueCount <= 1 ? "is" : "include";
  }
  if (operator === ArrayStringFilterOperator.NONE_OF) {
    return valueCount <= 1 ? "is not" : "exclude";
  }
  return arrayStringOperatorToText[operator];
}

function displayArrayItem(
  item: string,
  definition: ArrayStringTableFilter | undefined,
): string {
  if (definition?.display) {
    const displayed = definition.display(item);
    if (displayed && displayed !== item) {
      return displayed;
    }
  }
  if (item === SPAM_TAG) {
    return "Spam";
  }
  return item == null ? "" : String(item);
}

function formatRelativeTime(relativeTime: RelativeTime): string {
  if (isRelativeTimeRange(relativeTime)) {
    return `${formatSingleRelativeTime(relativeTime.end)} – ${formatSingleRelativeTime(relativeTime.start)}`;
  }
  return formatSingleRelativeTime(relativeTime);
}

function formatSingleRelativeTime(time: {
  value: number;
  unit: RelativeTimeUnit;
}): string {
  const unit =
    time.value === 1
      ? time.unit === RelativeTimeUnit.MINUTES
        ? "minute"
        : time.unit === RelativeTimeUnit.HOURS
          ? "hour"
          : "day"
      : time.unit;
  return `${time.value} ${unit} ago`;
}

function formatDateValue(
  filter: Extract<AdvancedFilterData, { type: AdvancedFilterType.DATE }>,
): string {
  if (
    filter.value === KnownDateFilterTimeFrame.RELATIVE &&
    filter.relativeTime
  ) {
    return formatRelativeTime(filter.relativeTime);
  }
  if (
    filter.value === KnownDateFilterTimeFrame.CUSTOM &&
    filter.customDate &&
    filter.customDate.length > 0
  ) {
    const format = (date: Date) =>
      getDateString(date, {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
      });
    if (filter.customDate.length === 2) {
      return `${format(filter.customDate[0])} - ${format(filter.customDate[1])}`;
    }
    return format(filter.customDate[0]);
  }
  return filter.value ? timeFrameToText[filter.value] : "";
}

function booleanDisplay(
  value: boolean | null | undefined,
  definition: ExistsTableFilter | BooleanTableFilter | undefined,
): string {
  if (value == null) {
    return "";
  }
  if (definition?.boolDisplay) {
    return value
      ? definition.boolDisplay.trueDisplay
      : definition.boolDisplay.falseDisplay;
  }
  return value ? "true" : "false";
}

function summarizeFilter(
  filter: AdvancedFilterData,
  definition: AdvancedTableFilter | undefined,
): SupportTicketViewFilterSummary {
  const Icon = definition?.Icon ?? FilterLinesIcon;
  const propertyName = definition?.displayName ?? filter.name;

  switch (filter.type) {
    case AdvancedFilterType.ARRAY_TO_STRING: {
      const arrayDefinition =
        definition?.type === AdvancedFilterType.ARRAY_TO_STRING
          ? definition
          : undefined;
      const values = filter.value ?? [];
      return {
        Icon,
        operatorLabel: arrayOperatorLabel(filter.operator, values.length),
        propertyName,
        valueLabel: valuelessOperators.includes(filter.operator)
          ? ""
          : values
              .map((value) => displayArrayItem(value, arrayDefinition))
              .filter(Boolean)
              .join(", "),
      };
    }
    case AdvancedFilterType.DATE:
      return {
        Icon,
        operatorLabel: dateOperatorToText[filter.operator],
        propertyName,
        valueLabel: formatDateValue(filter),
      };
    case AdvancedFilterType.NUMBER: {
      const values = filter.value ?? [];
      return {
        Icon,
        operatorLabel: numberOperatorToText[filter.operator],
        propertyName,
        valueLabel:
          filter.operator === NumberFilterOperator.BETWEEN &&
          values.length === 2
            ? `${values[0]} and ${values[1]}`
            : values[0] == null
              ? ""
              : String(values[0]),
      };
    }
    case AdvancedFilterType.STRING:
      return {
        Icon,
        operatorLabel: stringOperatorToText[filter.operator],
        propertyName,
        valueLabel: filter.value ?? "",
      };
    case AdvancedFilterType.EXISTS: {
      const existsDefinition =
        definition?.type === AdvancedFilterType.EXISTS ? definition : undefined;
      return {
        Icon,
        operatorLabel: existsOperatorToText[filter.operator],
        propertyName,
        valueLabel: booleanDisplay(filter.value, existsDefinition),
      };
    }
    case AdvancedFilterType.BOOLEAN: {
      const booleanDefinition =
        definition?.type === AdvancedFilterType.BOOLEAN
          ? definition
          : undefined;
      return {
        Icon,
        operatorLabel: booleanOperatorToText[filter.operator],
        propertyName,
        valueLabel: booleanDisplay(filter.value, booleanDefinition),
      };
    }
    default:
      assertNever(filter);
  }
}

export function partitionSummarizedSupportTicketViewFilters(
  filterGroup: FilterGroup,
  definitions: AdvancedTableFilter[] = getSupportTicketAdvancedFilters(),
): {
  defaultFilters: SupportTicketViewFilterSummary[];
  userFilters: SupportTicketViewFilterSummary[];
} {
  const defaultFilters: SupportTicketViewFilterSummary[] = [];
  const userFilters: SupportTicketViewFilterSummary[] = [];
  for (const filter of getSupportTicketLeafFilters(filterGroup)) {
    const summary = summarizeFilter(
      filter,
      definitions.find((definition) => definition.data.name === filter.name),
    );
    if (isRedoDefaultSupportTicketFilter(filter)) {
      defaultFilters.push(summary);
    } else {
      userFilters.push(summary);
    }
  }
  return { defaultFilters, userFilters };
}

export function summarizeSupportTicketViewFilters(
  filterGroup: FilterGroup,
  definitions: AdvancedTableFilter[] = getSupportTicketAdvancedFilters(),
): SupportTicketViewFilterSummary[] {
  const { userFilters, defaultFilters } =
    partitionSummarizedSupportTicketViewFilters(filterGroup, definitions);
  return [...userFilters, ...defaultFilters];
}
